import https from "node:https";
import { type CommonAction, success } from "@iflyrpa/share";
import type { ActiomCommonParams, AxiosProxyParams, BaiJiaHao, Toutiao, Weixin, Xiaohongshu } from "../../../types";
import { Http } from "../../../utils/http";

export type ttSearchAction = CommonAction<ActiomCommonParams, Toutiao.FansCount>;

export type xhsSearchAction = CommonAction<ActiomCommonParams, Xiaohongshu.FansCount>;

export interface GetWxFansSearchParams extends ActiomCommonParams {
    token: string | number;
}

export type wxSearchAction = CommonAction<GetWxFansSearchParams, Weixin.FansCount>;

export type bjhSearchAction = CommonAction<ActiomCommonParams, BaiJiaHao.FansCount>;

const generatorFormData = (data: Record<string, unknown>) => {
    const formData = new URLSearchParams();

    for (const [key, value] of Object.entries(data)) {
        formData.append(key, String(value));
    }

    return formData.toString();
};

export const TTFansExport: ttSearchAction = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://mp.toutiao.com/profile_v4/graphic/publish",
        },
    });

    const fans: Toutiao.FansCount = { digg_count: null, fans_count: null, following: null, description: "" };

    const userInfo = await http.api<{ code: number; total_fans_count: number; user_id_str: string }>({
        method: "get",
        url: "https://mp.toutiao.com/mp/agw/creator_center/user_info?app_id=1231",
        params: { app_id: 1231 },
    });

    async function getToutiaoToken(userId: string, cookies: Record<string, unknown>[]) {
        return new Promise((resolve, reject) => {
            const cookieStr = cookies.map((it: Record<string, unknown>) => `${it.name}=${it.value}`).join("; ");

            https
                .get(
                    `https://www.toutiao.com/c/user/${userId}/`,
                    {
                        headers: {
                            Cookie: cookieStr,
                            "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64)",
                        },
                    },
                    (res) => {
                        // 尝试从 Location 头中提取 token
                        const location = res.headers.location || "";
                        const match = location.match(/\/token\/([^/]+)/);
                        const token = match ? match[1] : "";

                        resolve(token);
                    },
                )
                .on("error", reject);
        });
    }

    const token = await getToutiaoToken(userInfo.user_id_str, params.cookies);

    type ttFansDetail = {
        digg_count: string;
        fans: string;
        following: string;
    };
    const fansDetail: { data: ttFansDetail; message: string } = await http.api({
        method: "POST",
        url: "https://www.toutiao.com/api/pc/user/fans_stat",
        data: generatorFormData({ token }),
    });

    const descriptionRes: { data: { media: { description: string } }; message: string } = await http.api({
        method: "GET",
        url: "https://mp.toutiao.com/mp/agw/media/edit_media_account_info",
        params: { app_id: 1231 },
    });

    fans.fans_count = Number(fansDetail.data.fans);
    fans.digg_count = Number(fansDetail.data.digg_count);
    fans.following = Number(fansDetail.data.following);
    fans.description = descriptionRes.data.media.description;

    return success(fansDetail.message === "success" ? fans : fans, "获取粉丝数成功！");
};

export const XhsFansExport: xhsSearchAction = async (_task, params) => {
    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        referer: "https://creator.xiaohongshu.com/new/home?source=official",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];
    const http = new Http(...args);

    const fans: Xiaohongshu.FansCount = { digg_count: null, fans_count: null, following: null, description: "" };

    const res = await http.api<{
        result: number;
        data: { follow_count: number; fans_count: number; faved_count: number; personal_desc: string };
    }>(
        {
            method: "get",
            url: "https://creator.xiaohongshu.com/api/galaxy/creator/home/personal_info",
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );

    fans.fans_count = Number(res.data.fans_count);
    fans.digg_count = Number(res.data.faved_count);
    fans.following = Number(res.data.follow_count);
    fans.description = res.data.personal_desc;

    return success(fans, `获取粉丝数成功！${_task.debug ? ` ${http.proxyInfo}` : ""}`);
};

export const WxFansExport: wxSearchAction = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        },
    });
    const fans: Weixin.FansCount = { digg_count: null, fans_count: null, following: null, description: "" };

    const htmlStr = await http.api<string>({
        method: "get",
        url: "https://mp.weixin.qq.com/cgi-bin/home",
        params: {
            t: "home/index",
            token: params.token,
            lang: "zh_CN",
        },
    });
    const match = htmlStr.match(/总用户数[\s\S]*?<mp-thousandth>(\d+)<\/mp-thousandth>/);
    if (match) {
        fans.fans_count = Number(match[1]);
    }

    const desc = await http.api<string>({
        url: "https://mp.weixin.qq.com/cgi-bin/settingpage",
        method: "get",
        params: {
            t: "setting/index",
            action: "index",
            token: params.token,
            lang: "zh_CN",
        },
    });
    const descMatch = desc.match(
        /<label[^>]*class="weui-desktop-setting__item__label"[^>]*>介绍<\/label>[\s\S]*?<div[^>]*class="weui-desktop-setting__item__info"[^>]*>(.*?)<\/div>/,
    );

    if (descMatch) {
        fans.description = descMatch[1].trim();
    }

    return success(fans, "获取粉丝数成功！");
};

export const BjhFansExport: bjhSearchAction = async (_task, params) => {
    const http = new Http({
        headers: {
            cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://baijiahao.baidu.com/builder/rc/home",
        },
    });
    const fans: BaiJiaHao.FansCount = { digg_count: null, fans_count: null, following: null, description: "" };

    const res = await http.api<{ errno: number; data: { total_fans: number } }>({
        method: "get",
        url: "https://baijiahao.baidu.com/cms-ui/rights/growth/get_info",
    });
    if (res.errno === 0) fans.fans_count = res.data.total_fans;

    const desc = await http.api<{ errno: number; data: { user: { wishes: string } } }>({
        method: "get",
        url: "https://baijiahao.baidu.com/builder/app/appinfo",
    });
    if (desc.errno === 0) fans.description = desc.data.user.wishes;

    return success(fans, "获取粉丝数成功！");
};
